// UserWizard.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUser } from './../../contexts/UserContext';
import countryFoodPreferences from '../../utils/country_food_preferences.json';
import { DEFAULT_COUNTRY } from '../../services/countryService';
import { getUserCountry, addCountryChangeListener, removeCountryChangeListener } from '../../services/apiService';
import { API_BASE_URL } from '../../services/backendClient';
import GroceryBudgetDrawer from './Drawers/GroceryBudgetDrawer';
import CookingStyleDrawer from './Drawers/CookingStyleDrawer';
import CuisineDrawer from './Drawers/CuisineDrawer';
import DietaryDrawer from './Drawers/DietaryDrawer';
import WhyNineMealsDrawer from './Drawers/WhyNineMealsDrawer';
import dietAndFoodPreferences from '../../utils/dietandfoodpreferences.json';
import Spinner from '../UI/Spinner';
import { 
  trackWizardStarted, 
  trackMealSetRequested,
  ENTRY_SOURCE,
  BUDGET_CATEGORY,
} from '../../analytics';

const API_URL = API_BASE_URL;

// Inline styles for wizard transitions
const wizardStyles = `
  .wizard-component {
    opacity: 0;
    transform: translateY(30px);
    animation: slideUpFadeIn 408ms cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
  }

  .wizard-header {
    animation-delay: 68ms;
  }

  .wizard-people-section {
    animation-delay: 136ms;
  }

  .wizard-budget-section {
    animation-delay: 204ms;
  }

  .wizard-cuisine-section {
    animation-delay: 272ms;
  }

  .wizard-dietary-section {
    animation-delay: 340ms;
  }

  .wizard-sticky-section {
    animation-delay: 408ms;
  }

  @keyframes slideUpFadeIn {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

// Dietary preference options from JSON file
export const dietaryOptions = dietAndFoodPreferences.dietaryOptions;


// People options
const peopleOptions = [1, 2, 3, 4, 5, 6];

// Helper to get budget options for a country

// Budget multipliers by household size (economies of scale)
const PEOPLE_BUDGET_MULTIPLIERS = { 1: 1.0, 2: 1.1, 3: 1.2, 4: 1.3, 5: 1.4, 6: 1.5 };
const getBudgetMultiplier = (n) => {
  if (PEOPLE_BUDGET_MULTIPLIERS[n]) return PEOPLE_BUDGET_MULTIPLIERS[n];
  // Extrapolate beyond 6 with the same step (+0.7 per additional person)
  if (n > 6) return PEOPLE_BUDGET_MULTIPLIERS[6] + 0.7 * (n - 6);
  // Fallback
  return 1.0;
};

// Approximate budget share per meal type — used when calculating cookFor2Days surcharge
const MEAL_TYPE_BUDGET_WEIGHTS = { breakfast: 0.20, lunch: 0.35, dinner: 0.45 };

// Returns a multiplier > 1.0 for each meal type with cookFor2Days enabled.
// Example: lunch + dinner both on 2 days → 1.0 + 0.35 + 0.45 = 1.80
const getCookFor2DaysMultiplier = (cookingStyle) => {
  if (!cookingStyle) return 1.0;
  let extra = 0;
  Object.entries(MEAL_TYPE_BUDGET_WEIGHTS).forEach(([mealType, weight]) => {
    if (cookingStyle[mealType]?.cookFor2Days) extra += weight;
  });
  return 1.0 + extra;
};

function getBudgetOptions(country, numberOfPeople = 1, t = null, cookingStyle = null) {
  const countryData = countryFoodPreferences[country] || countryFoodPreferences[DEFAULT_COUNTRY];
  // Use countryData.currency if present, otherwise fallback to $ (for legacy)
  const currency = countryData.currencySymbol || '$';
  const budgetObj = countryData.budget || {};
  // Use translations if t function is provided, otherwise fallback to English labels
  const labels = {
    survival: t ? t('budget.survival') : 'Survival',
    budget: t ? t('budget.budget') : 'Budget',
    standard: t ? t('budget.standard') : 'Standard',
    gourmet: t ? t('budget.gourmet') : 'Gourmet',
    luxury: t ? t('budget.luxury') : 'Luxury',
  };
  const multiplier = getBudgetMultiplier(numberOfPeople) * getCookFor2DaysMultiplier(cookingStyle);
  return Object.entries(budgetObj)
    .filter(([key]) => key !== 'luxury' && key !== 'survival') // Hide luxury and survival options
    .map(([key, [min, max]]) => {
    // Apply non-linear multiplier by household size
    const adjustedMin = Math.round(min * multiplier);
    const adjustedMax = max !== null ? Math.round(max * multiplier) : null;

    // Format: "Standard < 110€" or "Standard < $110"
    const categoryLabel = labels[key] || key.charAt(0).toUpperCase() + key.slice(1);
    let label;
    
    if (adjustedMax !== null) {
      label = country === 'USA'
        ? `${categoryLabel} < ${currency}${adjustedMax}`
        : `${categoryLabel} < ${adjustedMax}${currency}`;
    } else {
      // For unlimited budget (no max)
      label = country === 'USA'
        ? `${categoryLabel} ${currency}${adjustedMin}+`
        : `${categoryLabel} ${adjustedMin}${currency}+`;
    }

    return {
      key,
      min: adjustedMin,
      max: adjustedMax,
      label: label,
      currency,
      display: label
    };
  });
}

const UserWizard = ({ onFinish, onBack }) => {
  const { t } = useTranslation('wizard');
  const { user, budget: contextBudget, numberOfPeople: contextNumberOfPeople, selectedCuisine: contextSelectedCuisine, dietaryPreferences: contextDietaryPreferences, cookingStyle: contextCookingStyle, setBudget, setNumberOfPeople, setSelectedCuisine, setDietaryPreferences, setCookingStyle, savePreferences } = useUser();

  // Default cooking style
  const defaultCookingStyle = { 
    breakfast: { cookingTime: 'standard', cookFor2Days: false },
    lunch: { cookingTime: 'standard', cookFor2Days: false },
    dinner: { cookingTime: 'standard', cookFor2Days: false }
  };

  // Main state values - initialize with context values
  const [budget, updateBudget] = useState(contextBudget || "75");
  const [numberOfPeople, updateNumberOfPeople] = useState(contextNumberOfPeople || 1);
  const [cookingStyle, updateCookingStyle] = useState(contextCookingStyle || defaultCookingStyle);
  const [selectedCuisines, setSelectedCuisines] = useState(() => {
    // Initialize cuisine selection from context
    if (contextSelectedCuisine && contextSelectedCuisine !== 'Italian') {
      if (contextSelectedCuisine === 'Surprise Me') {
        return ['Surprise Me'];
      } else {
        // Split multiple cuisines if they exist
        const cuisines = contextSelectedCuisine.split(", ").filter(c => c.trim());
        return cuisines.length > 0 ? cuisines : ["Surprise Me"];
      }
    }
    return ["Surprise Me"];
  });
  const [selectedDietaryPreferences, setSelectedDietaryPreferences] = useState(() => {
    // Initialize dietary preferences from context
    if (contextDietaryPreferences && Array.isArray(contextDietaryPreferences)) {
      // Migrate old 'none' key to new noneFood/noneDiet format
      const hasOldNone = contextDietaryPreferences.some(p => p.key === 'none');
      if (hasOldNone) {
        return getDefaultNoneOptions();
      }
      return contextDietaryPreferences;
    } else if (contextDietaryPreferences && contextDietaryPreferences !== 'None' && contextDietaryPreferences !== 'No preference') {
      // Handle legacy string format
      const preference = dietaryOptions.find(opt => opt.label === contextDietaryPreferences || opt.key === contextDietaryPreferences);
      return preference ? [preference] : getDefaultNoneOptions();
    }
    return getDefaultNoneOptions(); // Default to both None options
  });
  
  // Helper to get default none options (both noneFood and noneDiet)
  function getDefaultNoneOptions() {
    const noneFood = dietaryOptions.find(opt => opt.key === 'noneFood');
    const noneDiet = dietaryOptions.find(opt => opt.key === 'noneDiet');
    return [noneFood, noneDiet].filter(Boolean);
  }
  const [country, setCountry] = useState("USA");
  // UI state
  const [activeDrawer, setActiveDrawer] = useState(null);
  const [isSavingPreferences, setIsSavingPreferences] = useState(false);
  const [isCreatingMeals, setIsCreatingMeals] = useState(false);
  // Cuisines state
  const [cuisineOptions, setCuisineOptions] = useState([]);
  const [isLoadingCuisines, setIsLoadingCuisines] = useState(false);
  // Budget options state
  const [budgetOptions, setBudgetOptions] = useState([]);
  // Meal plan limit state
  const [triesLeft, setTriesLeft] = useState(null);

  // Fetch meal plan limit for logged-in users
  useEffect(() => {
    const checkMealPlanLimit = async () => {
      // For guest users, we don't show the count in the wizard.
      // The limit is enforced on the generation screen.
      if (!user) {
        // Set triesLeft to a non-zero number for guests to enable the button
        setTriesLeft(3); 
        return;
      }
      try {
        const response = await fetch(`${API_URL}/api/meal-plan-limit/check`, {
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          const remaining = data.triesRemaining !== undefined ? data.triesRemaining : 0;
          setTriesLeft(remaining);
        } else {
          console.error('Failed to fetch meal plan limit status.');
          setTriesLeft(0); // Assume worst case on error
        }
      } catch (error) {
        console.error('Error fetching meal plan limit:', error);
        setTriesLeft(0); // Assume worst case on error
      }
    };

    checkMealPlanLimit();
  }, [user]);

  // Initialize cuisine options based on country
  useEffect(() => {
    const countryData = countryFoodPreferences[country] || countryFoodPreferences[DEFAULT_COUNTRY];
    
    setIsLoadingCuisines(true);
    
    // Add surprise option first
    const options = [
      { 
        name: "Surprise Me",
        description: "All cuisines"
      }
    ];
    
    // Add country-specific cuisine options
    if (countryData && countryData.favoriteCuisines) {
      countryData.favoriteCuisines.forEach(cuisine => {
        options.push({
          name: cuisine.name,
          description: cuisine.description
        });
      });
    }
    
    setCuisineOptions(options);
    setIsLoadingCuisines(false);
  }, [country]);

  // Country change listener
  useEffect(() => {
    // Initialize with current country
    const initializeCountry = async () => {
      try {
        const currentCountry = await getUserCountry();
        setCountry(currentCountry || DEFAULT_COUNTRY);
      } catch (error) {
        console.error('Error getting user country:', error);
        setCountry(DEFAULT_COUNTRY);
      }
    };
    
    initializeCountry();
    
    // Handler for country changes coming from CountrySelector
    const handleCountryChange = (newCountry) => {
      console.log(`UserWizard: Country changed to ${newCountry}, updating cuisines`);
      setCountry(newCountry);
      
      // Reset cuisine selection when country changes
      setSelectedCuisines(["Surprise Me"]);
    };

    // Add country change listener
    addCountryChangeListener(handleCountryChange);

    // Track Wizard Started (only on initial mount)
    const hasExistingPrefs = !!(contextBudget || contextSelectedCuisine);
    trackWizardStarted({
      entry_source: ENTRY_SOURCE.LANDING_CTA, // Most common entry
      prefill_present: hasExistingPrefs,
    });

    // Cleanup listener on unmount
    return () => {
      removeCountryChangeListener(handleCountryChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Primitive key derived from cookFor2Days flags — safe to use as useEffect dependency
  const cookFor2DaysKey = `${cookingStyle.breakfast?.cookFor2Days}-${cookingStyle.lunch?.cookFor2Days}-${cookingStyle.dinner?.cookFor2Days}`;

  // Update budget options when country, number of people, or cookFor2Days changes
  useEffect(() => {
    console.log(`Budget options useEffect triggered. Country: ${country}, People: ${numberOfPeople}, cookFor2DaysKey: ${cookFor2DaysKey}`);
    console.log(`Current budget state:`, budget);
    
    const options = getBudgetOptions(country, numberOfPeople, t, cookingStyle);
    console.log(`Generated budget options:`, options);
    setBudgetOptions(options);
    
    // Preserve the user's selected budget category when updating options
    if (budget && typeof budget === 'object') {
      let budgetKey = budget.key;
      
      // If budget doesn't have a key, try to infer it from the label
      // New format: "Standard < 110€" or "Standard < $110"
      if (!budgetKey && budget.label) {
        // Extract the category name (first word before <)
        const categoryName = budget.label.split('<')[0].trim().toLowerCase();
        budgetKey = categoryName === 'survival' ? 'survival' :
                   categoryName === 'budget' ? 'budget' :
                   categoryName === 'standard' ? 'standard' :
                   categoryName === 'gourmet' ? 'gourmet' :
                   categoryName === 'luxury' ? 'luxury' : 'standard';
      }
      
      console.log(`Looking for budget category: ${budgetKey}`);
      // Find the same budget category in the new options
      const sameCategory = options.find(opt => opt.key === budgetKey);
      console.log(`Found same category:`, sameCategory);
      if (sameCategory) {
        console.log(`Updating budget from:`, budget, `to:`, sameCategory);
        updateBudget(sameCategory);
        // Don't update context here - only update local state during initialization
        // Context will be updated on submit
        return;
      }
    }
    
    // Only default to Standard if no budget is selected yet
    if (!budget || (typeof budget === 'string')) {
      console.log(`No budget selected, defaulting to standard`);
      const standard = options.find(opt => opt.key === 'standard');
      if (standard) {
        console.log(`Setting standard budget:`, standard);
        updateBudget(standard);
        // Don't update context here - only update local state during initialization
      } else if (options.length > 0) {
        console.log(`Setting first available budget:`, options[0]);
        updateBudget(options[0]);
        // Don't update context here - only update local state during initialization
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, numberOfPeople, t, cookFor2DaysKey]);

  // Initialize with context values on mount ONLY (not on every context change)
  useEffect(() => {
    // This effect should only run once on mount to initialize from context
    // After that, the local state is the source of truth
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run on mount

  // Sync cuisine selection from context when it changes - but not if it's the same
  useEffect(() => {
    // Only sync from context if it's different AND the wizard just mounted/opened
    // Don't sync during normal operation to avoid overwriting user selections
    const currentCuisineStr = selectedCuisines.join(", ");
    if (contextSelectedCuisine && 
        contextSelectedCuisine !== currentCuisineStr &&
        contextSelectedCuisine !== 'Italian') { // Ignore default value
      if (contextSelectedCuisine === 'Surprise Me') {
        setSelectedCuisines(['Surprise Me']);
      } else {
        const cuisines = contextSelectedCuisine.split(", ").filter(c => c.trim());
        if (cuisines.length > 0) {
          setSelectedCuisines(cuisines);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount, not on context changes

  // Drawer handlers
  const openDrawer = (drawer) => {
    setActiveDrawer(drawer);
  };

  const closeDrawer = () => {
    setActiveDrawer(null);
  };

  // Save preferences and close drawer
  const handleDrawerSave = async () => {
    setIsSavingPreferences(true);
    try {
      await savePreferences();
    } finally {
      setIsSavingPreferences(false);
      setActiveDrawer(null);
    }
  };

  // Budget handlers
  const handleBudgetUpdate = (option) => {
    updateBudget(option);
    setBudget(option); // Save the selected budget to UserContext
  };

  // People handlers
  const handlePeopleSelect = (value) => {
    console.log(`Changing people from ${numberOfPeople} to ${value}`);
    console.log(`Current budget:`, budget);
    
    // Update local state first
    updateNumberOfPeople(value);
    
    // Update context immediately so the change persists
    setNumberOfPeople(value);
    
    // Update budget immediately for the new people count
    if (budget && typeof budget === 'object') {
      let budgetKey = budget.key;
      
      // If budget doesn't have a key, try to infer it from the label
      // New format: "Standard < 110€" or "Standard < $110"
      if (!budgetKey && budget.label) {
        // Extract the category name (first word before <)
        const categoryName = budget.label.split('<')[0].trim().toLowerCase();
        budgetKey = categoryName === 'survival' ? 'survival' :
                   categoryName === 'budget' ? 'budget' :
                   categoryName === 'standard' ? 'standard' :
                   categoryName === 'gourmet' ? 'gourmet' :
                   categoryName === 'luxury' ? 'luxury' : 'standard';
      }
      
      const newOptions = getBudgetOptions(country, value, t, cookingStyle);
      const sameCategory = newOptions.find(opt => opt.key === budgetKey);
      console.log(`New budget for ${value} people:`, sameCategory);
      
      if (sameCategory) {
        // Update both local and context state
        updateBudget(sameCategory);
        setBudget(sameCategory);
      }
    }
  };

  // Set all cuisines at once (used by drawer save)
  const setCuisinesAll = (cuisines) => {
    setSelectedCuisines(cuisines);
    const cuisineStr = cuisines.length > 0 ? cuisines.join(", ") : "Surprise Me";
    setSelectedCuisine(cuisineStr);
  };

  // Set all dietary preferences at once (used by drawer save)
  const setDietaryPreferencesAll = (preferences) => {
    setSelectedDietaryPreferences(preferences);
    setDietaryPreferences(preferences);
  };

  // Set all cooking style at once (used by drawer save)
  const setCookingStyleAll = (newCookingStyle) => {
    updateCookingStyle(newCookingStyle);
    setCookingStyle(newCookingStyle);
  };

  // Get display items for selected options (returns arrays for chip rendering)
  const getSelectedCuisineDisplay = () => {
    if (selectedCuisines.includes("Surprise Me")) {
      return [t('cuisine.surpriseMe')];
    }
    if (selectedCuisines.length === 0) {
      return [t('cuisine.selectCuisine')];
    }
    return selectedCuisines.map(name => t(`cuisine.types.${name}`, name));
  };

  const getSelectedDietaryDisplay = () => {
    // Filter out None options to get real preferences
    // Check both isNoneOption flag and key patterns for backwards compatibility
    const realPreferences = selectedDietaryPreferences.filter(pref => 
      !pref.isNoneOption && 
      pref.key !== 'none' && 
      pref.key !== 'noneFood' && 
      pref.key !== 'noneDiet'
    );
    
    // If there are real preferences, show them (without None)
    if (realPreferences.length > 0) {
      return realPreferences.map(pref => t(`dietary.options.${pref.key}`, pref.label));
    }
    
    // If all are None options, just show "None" once
    return [t('dietary.options.none', 'None')];
  };

  const getCookingStyleDisplay = () => {
    const parts = [];
    const mealTypes = ['breakfast', 'lunch', 'dinner'];
    
    mealTypes.forEach(mealType => {
      const style = cookingStyle[mealType];
      const complexity = style?.cookingTime || 'standard';
      const cookFor2Days = style?.cookFor2Days || false;
      
      // Only show non-standard settings
      if (complexity !== 'standard' || cookFor2Days) {
        // Get the display label for complexity + meal type
        let label = '';
        if (complexity === 'simple') {
          label = t(`cookingStyle.display.simple${mealType.charAt(0).toUpperCase() + mealType.slice(1)}`);
        } else if (complexity === 'advanced') {
          label = t(`cookingStyle.display.advanced${mealType.charAt(0).toUpperCase() + mealType.slice(1)}`);
        } else {
          // Standard complexity but has 2 days - just show meal name
          label = t(`cookingStyle.${mealType}`);
        }
        
        parts.push({
          label,
          has2Days: cookFor2Days
        });
      }
    });
    
    if (parts.length === 0) return [{ label: t('cookingStyle.display.allStandard'), has2Days: false }];
    return parts;
  };

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsCreatingMeals(true);
    
    try {
      // Format cuisine selection (needed before save and context update)
      const cuisineStr = selectedCuisines.includes("Surprise Me") 
        ? "Surprise Me" 
        : selectedCuisines.join(", ");

      // Save current wizard values explicitly BEFORE updating context.
      // React 18 batches all set* calls inside async handlers — they are applied after
      // the handler returns, so savePreferences() would capture stale context values.
      // Passing explicitData bypasses the stale closure issue.
      await savePreferences({
        numberOfPeople,
        budget,
        selectedCuisine: cuisineStr,
        dietaryPreferences: selectedDietaryPreferences,
        cookingStyle
      });

      // Sync wizard values into context (for the rest of the session)
      setBudget(budget);
      setNumberOfPeople(numberOfPeople);
      setCookingStyle(cookingStyle);
      setSelectedCuisine(cuisineStr);
      setDietaryPreferences(selectedDietaryPreferences);
      
      // Get currency for analytics
      const countryData = countryFoodPreferences[country] || countryFoodPreferences[DEFAULT_COUNTRY];
      const currency = countryData.currencySymbol || '$';
      
      // Determine budget category
      const budgetKey = budget?.key || 'standard';
      const budgetCategoryMap = {
        'budget': BUDGET_CATEGORY.BUDGET,
        'standard': BUDGET_CATEGORY.STANDARD,
        'gourmet': BUDGET_CATEGORY.GOURMET,
      };
      
      // Track Meal Set Requested (commitment moment)
      trackMealSetRequested({
        household_size: numberOfPeople,
        budget_category: budgetCategoryMap[budgetKey] || BUDGET_CATEGORY.STANDARD,
        budget_amount: budget?.max || budget?.min || 0,
        meals_breakfast_per_day: cookingStyle.breakfast?.cookFor2Days ? 2 : 1,
        meals_lunch_per_day: cookingStyle.lunch?.cookFor2Days ? 2 : 1,
        meals_dinner_per_day: cookingStyle.dinner?.cookFor2Days ? 2 : 1,
        cuisines_count: selectedCuisines.includes("Surprise Me") ? 0 : selectedCuisines.length,
        dietary_count: selectedDietaryPreferences.filter(p => !p.isNoneOption).length,
        tries_remaining: triesLeft || 0,
        primary_cuisine: selectedCuisines[0] !== "Surprise Me" ? selectedCuisines[0] : null,
        has_dietary_restrictions: selectedDietaryPreferences.some(p => !p.isNoneOption),
        country,
        currency,
      });
      
      onFinish();
    } catch (error) {
      console.error('Error creating meals:', error);
      setIsCreatingMeals(false);
    }
  };

    return (
    <>
      <style>{wizardStyles}</style>
      {/* Main wizard content */}
      <div className="min-h-safe-screen flex flex-col pb-[80px]"
      >
        <div className="flex-1 px-6 py-6 pb-8 relative max-w-5xl w-full mx-auto flex flex-col">
          
          {/* Main wizard content */}
          <div className="pb-0 md:pb-20 flex flex-col justify-end items-end flex-1">
            <div className="w-full wizard-component wizard-header">
              <h1 className="
                  text-[var(--Color-Grays-Gray-10,#0D0C07)]
                  text-[18.8px]
                  font-bold
                  leading-[36px]
                  mb-0
              ">{t('title')}</h1>
              <p className="
                  text-[rgba(13,12,7,0.8)]
                  text-[15.2px]
                  font-normal
                  leading-[28px]
                  mb-5
              ">
                {t('subtitle')}
              </p>
            </div>
          
          {/* Grocery Budget section (People + Budget merged) - First */}
          <div 
            className="
              wizard-component wizard-budget-section
              flex
              cursor-pointer
              min-h-[48px]
              pb-4
              p-3
              items-start
              self-stretch
              rounded-[var(--Border-Radius-Outer,12px)]
              border-[0.5px]
              border-[var(--Color-Grays-Gray-8,#56554D)]
              bg-[var(--Color-Grays-Gray-025,#F8F8F7)]
              md:hover:bg-white
              transition-all
              shadow-sm
              mb-3
            "
            onClick={() => openDrawer('groceryBudget')}
          >
            <div className="w-7 h-7 flex-shrink-0 flex items-center justify-center mr-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10.0001 1.66699V18.3337M14.1668 4.16699H7.91679C7.14324 4.16699 6.40138 4.47428 5.85439 5.02126C5.30741 5.56825 5.00012 6.31011 5.00012 7.08366C5.00012 7.85721 5.30741 8.59907 5.85439 9.14605C6.40138 9.69303 7.14324 10.0003 7.91679 10.0003H12.0835C12.857 10.0003 13.5989 10.3076 14.1459 10.8546C14.6928 11.4016 15.0001 12.1434 15.0001 12.917C15.0001 13.6905 14.6928 14.4324 14.1459 14.9794C13.5989 15.5264 12.857 15.8337 12.0835 15.8337H5.00012" stroke="#0D0C07" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="flex-grow">
              <h2 className="
                  text-[var(--Color-Grays-Gray-10,#0D0C07)]
                  text-[15.2px]
                  font-semibold
                  leading-[28px]
                  mb-1
              ">{t('sections.groceryBudget')}</h2>
              <div className="flex flex-wrap gap-2">
                <div
                    className="
                      text-[var(--Color-Grays-Gray-10,#0D0C07)]
                      text-[14px]
                      font-normal
                      leading-[24px]
                      bg-[var(--Color-Brand-Primary,#FFCB00)]
                      px-3 py-1
                      rounded-[6px]
                      inline-block
                    "
                  >
                  {budget?.label || ''}
                </div>
                <div
                    className="
                      text-[var(--Color-Grays-Gray-10,#0D0C07)]
                      text-[14px]
                      font-normal
                      leading-[24px]
                      bg-[var(--Color-Brand-Primary,#FFCB00)]
                      px-3 py-1
                      rounded-[6px]
                      inline-block
                    "
                  >
                  {numberOfPeople} {numberOfPeople === 1 ? t('people.person') : t('people.people')}
                </div>
              </div>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); openDrawer('whyNineMeals'); }}
                className="mt-2 inline-block text-left text-[13px] font-medium leading-[20px] text-[#0D0C07] pb-[2px] border-b-[1.5px] border-[var(--Color-Brand-Primary,#FFCB00)] hover:border-[#E6B700] focus:outline-none"
              >
                {t('whyNineMeals.triggerLink')}
              </button>
            </div>
          </div>
          
          {/* Cooking Frequency section - Second */}
          <div 
            className="
              wizard-component wizard-people-section
              flex
              cursor-pointer
              min-h-[48px]
              pb-4
              p-3
              items-start
              self-stretch
              rounded-[var(--Border-Radius-Outer,12px)]
              border-[0.5px]
              border-[var(--Color-Grays-Gray-8,#56554D)]
              bg-[var(--Color-Grays-Gray-025,#F8F8F7)]
              md:hover:bg-white
              transition-all
              shadow-sm
              mb-3
            "
            onClick={() => openDrawer('cookingStyle')}
          >
            <div className="w-7 h-7 flex-shrink-0 flex items-center justify-center mr-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 10C18.3333 5.39763 14.6024 1.66667 10 1.66667C5.39763 1.66667 1.66667 5.39763 1.66667 10C1.66667 14.6024 5.39763 18.3333 10 18.3333Z" stroke="#0D0C07" strokeWidth="1.75"/>
                <path d="M10 5V10L13.3333 11.6667" stroke="#0D0C07" strokeWidth="1.75" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="flex-grow">
              <h2 className="
                  text-[var(--Color-Grays-Gray-10,#0D0C07)]
                  text-[15.2px]
                  font-semibold
                  leading-[28px]
                  mb-1
              ">{t('sections.cookingStyle')}</h2>
              <div className="flex flex-wrap gap-2">
                {getCookingStyleDisplay().map((item, index) => (
                  <div
                    key={index}
                    className="
                      text-[var(--Color-Grays-Gray-10,#0D0C07)]
                      text-[14px]
                      font-normal
                      leading-[24px]
                      bg-[var(--Color-Brand-Primary,#FFCB00)]
                      px-3 py-1
                      rounded-[6px]
                      inline-block
                    "
                  >
                    {item.label}
                    {item.has2Days && (
                      <>
                        <span className="text-[rgba(13,12,7,0.7)]"> • </span>
                        <span>2 {t('cookingStyle.display.days')}</span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Cuisine section - Third */}
          <div 
            className="
              wizard-component wizard-cuisine-section
              flex
              cursor-pointer
              min-h-[48px]
              pb-4
              p-3
              items-start
              self-stretch
              rounded-[var(--Border-Radius-Outer,12px)]
              border-[0.5px]
              border-[var(--Color-Grays-Gray-8,#56554D)]
              bg-[var(--Color-Grays-Gray-025,#F8F8F7)]
              md:hover:bg-white
              transition-all
              shadow-sm
              mb-3
            "
            onClick={() => openDrawer('cuisine')}
          >
            <div className="w-7 h-7 flex-shrink-0 flex items-center justify-center mr-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M5.00016 14.1671H15.0002M14.1668 17.5005C14.3878 17.5005 14.5998 17.4127 14.7561 17.2564C14.9124 17.1001 15.0002 16.8881 15.0002 16.6671V12.2088C15.0002 11.828 15.2635 11.5055 15.606 11.3413C16.3151 11.003 16.882 10.4251 17.2065 9.70952C17.531 8.99397 17.5924 8.18678 17.3797 7.43042C17.167 6.67405 16.694 6.01712 16.0441 5.57557C15.3942 5.13402 14.6092 4.93624 13.8277 5.01712C13.5058 4.26896 12.9717 3.63153 12.2915 3.18365C11.6112 2.73577 10.8146 2.49707 10.0002 2.49707C9.1857 2.49707 8.3891 2.73577 7.70885 3.18365C7.0286 3.63153 6.49452 4.26896 6.17266 5.01712C5.39148 4.9368 4.60702 5.13487 3.9576 5.5764C3.30818 6.01793 2.8355 6.67456 2.6229 7.43054C2.41029 8.18651 2.47142 8.99327 2.7955 9.70858C3.11958 10.4239 3.6858 11.0018 4.39433 11.3405C4.73683 11.5055 5.00016 11.828 5.00016 12.208V16.6671C5.00016 16.8881 5.08796 17.1001 5.24424 17.2564C5.40052 17.4127 5.61248 17.5005 5.83349 17.5005H14.1668Z" stroke="#0D0C07" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="flex-grow">
              <h2 className="
                  text-[var(--Color-Grays-Gray-10,#0D0C07)]
                  text-[15.2px]
                  font-semibold
                  leading-[28px]
                  mb-1
              ">{t('sections.cuisine')}</h2>
              <div className="flex flex-wrap gap-2">
                {getSelectedCuisineDisplay().map((item, index) => (
                  <div
                    key={index}
                    className="
                      text-[var(--Color-Grays-Gray-10,#0D0C07)]
                      text-[14px]
                      font-normal
                      leading-[24px]
                      bg-[var(--Color-Brand-Primary,#FFCB00)]
                      px-3 py-1
                      rounded-[6px]
                      inline-block
                    "
                  >
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Dietary preferences section - Fourth */}
          <div 
            className="
              wizard-component wizard-dietary-section
              flex
              cursor-pointer
              min-h-[48px]
              pb-4
              p-3
              items-start
              self-stretch
              rounded-[var(--Border-Radius-Outer,12px)]
              border-[0.5px]
              border-[var(--Color-Grays-Gray-8,#56554D)]
              bg-[var(--Color-Grays-Gray-025,#F8F8F7)]
              md:hover:bg-white
              transition-all
              shadow-sm
            "
            onClick={() => openDrawer('dietary')}
          >
            <div className="w-7 h-7 flex-shrink-0 flex items-center justify-center mr-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <g clipPath="url(#clip0_1850_12962)">
                  <path d="M13.3334 6.66699C16.6667 6.66699 18.3334 5.00033 18.3334 1.66699C15.0001 1.66699 13.3334 3.33366 13.3334 6.66699ZM13.3334 6.66699C11.2501 10.417 10.7501 12.6503 10.0001 18.3337C9.90402 15.1172 9.10883 11.9603 7.66976 9.08215C6.23069 6.20401 4.18225 3.67369 1.66675 1.66699M14.5084 2.99199C12.7791 1.87955 10.6996 1.44573 8.66971 1.77397C6.63985 2.1022 4.80302 3.16932 3.51245 4.77009C2.22188 6.37087 1.5687 8.39227 1.67853 10.4456C1.78836 12.4988 2.65344 14.439 4.10741 15.893C5.56138 17.347 7.50157 18.212 9.55485 18.3219C11.6081 18.4317 13.6295 17.7785 15.2303 16.488C16.8311 15.1974 17.8982 13.3606 18.2264 11.3307C18.5547 9.30085 18.1209 7.2213 17.0084 5.49199" stroke="#0D0C07" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                </g>
                <defs>
                  <clipPath id="clip0_1850_12962">
                    <rect width="20" height="20" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
            </div>
            <div className="flex-grow">
              <h2 className="
                  text-[var(--Color-Grays-Gray-10,#0D0C07)]
                  text-[15.2px]
                  font-semibold
                  leading-[28px]
                  mb-1
              ">{t('sections.dietary')}</h2>
              <div className="flex flex-wrap gap-2">
                {getSelectedDietaryDisplay().map((item, index) => (
                  <div
                    key={index}
                    className="
                      text-[var(--Color-Grays-Gray-10,#0D0C07)]
                      text-[14px]
                      font-normal
                      leading-[24px]
                      bg-[var(--Color-Brand-Primary,#FFCB00)]
                      px-3 py-1
                      rounded-[6px]
                      inline-block
                    "
                  >
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* Sticky button - positioned at bottom with inline styles */}        <div
          className="
            fixed bottom-[16px] left-[20px] right-[20px]
            py-2 px-2 
            flex justify-center
            bg-[linear-gradient(135deg,_rgba(243,243,242,0.8)_0%,_rgba(243,243,242,0.9)_100%)]
            backdrop-blur-[64px]
            z-50
            max-w-5xl
            mx-auto
            rounded-[96px]
            border-[1px] border-[#fff]
            shadow-[0px_0px_4px_0px_rgba(13,12,7,0.1),_1px_-2px_16px_0px_rgba(13,12,7,0.1)]
          " 
        >
        <div className="w-full max-w-5xl">
          <button
            className={`
              relative
              inline-flex justify-center items-center gap-1
              h-[48px] px-[24px] py-[14px]
              rounded-[32px]
              border-[1px] border-[linear-gradient(200deg,_#090_0%,_#060_100%)]
              bg-[#090]
              text-[#FFF] text-center
              text-[14.2px] font-semibold leading-[28px]
              transition-colors
              w-full
              shadow-[0_2px_16px_0_rgba(0,0,0,0.12)_inset,_0_0_32px_0_rgba(255,255,255,0.4)_inset,_0_0px_0px_2px_rgba(0,102,0,0.4)]
              ${(triesLeft === 0 || isCreatingMeals)
                ? 'cursor-not-allowed opacity-60 bg-[#090]'
                : 'cursor-pointer bg-[linear-gradient(200deg,_#090_0%,_#060_100%)] md:hover:border-[1px] md:hover:border-[#060] md:hover:bg-[#060]'
              }
            `}
            onClick={isCreatingMeals ? undefined : handleSubmit}
            disabled={triesLeft === 0 || isCreatingMeals}
          >
            {isCreatingMeals ? (
              <>
                <Spinner size="sm" color="#FFF" />
                <span>{t('creating')}</span>
              </>
            ) : (
              <>
               {/* <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[12px] font-semibold leading-[12px] opacity-70">
                  {triesLeft} {triesLeft === 1 ? t('try') : t('tries')}
                </span> */}
                <span>{t('createButton')}</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M11.3816 5.54742C11.702 5.22734 12.2089 5.20753 12.5525 5.48785L12.6189 5.54742L18.4519 11.3814C18.7936 11.7231 18.7936 12.277 18.4519 12.6187L12.6189 18.4517L12.5525 18.5123C12.2088 18.7925 11.7019 18.7721 11.3816 18.4517C11.0399 18.11 11.0399 17.5561 11.3816 17.2144L15.7214 12.8746H6.16675C5.6835 12.8746 5.29175 12.4828 5.29175 11.9996C5.29192 11.5165 5.68361 11.1246 6.16675 11.1246H15.7205L11.3816 6.7857L11.321 6.7193C11.0406 6.37562 11.0612 5.86782 11.3816 5.54742Z" fill="currentColor" />
                </svg>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Drawers */}
      <GroceryBudgetDrawer 
        isOpen={activeDrawer === 'groceryBudget'}
        numberOfPeople={numberOfPeople}
        peopleOptions={peopleOptions}
        handlePeopleSelect={handlePeopleSelect}
        budget={budget}
        budgetOptions={budgetOptions}
        handleBudgetUpdate={handleBudgetUpdate}
        closeDrawer={closeDrawer}
        onSave={handleDrawerSave}
        isSaving={isSavingPreferences}
        country={country}
        getBudgetOptionsForPeople={(numPeople) => getBudgetOptions(country, numPeople, t, cookingStyle)}
      />
      <CookingStyleDrawer
        isOpen={activeDrawer === 'cookingStyle'}
        cookingStyle={cookingStyle}
        setCookingStyleAll={setCookingStyleAll}
        closeDrawer={closeDrawer}
        onSave={handleDrawerSave}
        isSaving={isSavingPreferences}
        budget={budget}
        country={country}
      />
      <CuisineDrawer
        isOpen={activeDrawer === 'cuisine'}
        cuisineOptions={cuisineOptions}
        selectedCuisines={selectedCuisines}
        isLoadingCuisines={isLoadingCuisines}
        setCuisinesAll={setCuisinesAll}
        closeDrawer={closeDrawer}
        onSave={handleDrawerSave}
        isSaving={isSavingPreferences}
      />
      <DietaryDrawer
        isOpen={activeDrawer === 'dietary'}
        dietaryOptions={dietaryOptions}
        selectedDietaryPreferences={selectedDietaryPreferences}
        setDietaryPreferencesAll={setDietaryPreferencesAll}
        closeDrawer={closeDrawer}
        onSave={handleDrawerSave}
        isSaving={isSavingPreferences}
      />
      <WhyNineMealsDrawer
        isOpen={activeDrawer === 'whyNineMeals'}
        onClose={closeDrawer}
      />
    </>
  );
};

export default UserWizard;